import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link } from "react-router";
import { Botao, Aviso } from "../ui";
import { mensagemDoConsole } from "../../nucleo/console";
import { useQueryClient } from "@tanstack/react-query";
import {
  CHAVE_DO_WEBCHAT,
  acrescentarMensagem,
  frasePorVoltar,
  mudaOEstado,
  useMensagensDaConversa,
  useAbrirConversa,
  useAvaliarConversa,
  useCancelarConversa,
  useEstadoDoChat,
  useConversaAoVivo,
  type ConversaDoCliente,
  type EstadoDoChat,
  type MensagemDoChat,
} from "../../nucleo/webchat";
import { Conversa } from "./Conversa";

/**
 * O chat do cliente: um botão fixo no canto da área `/app` que abre o painel da conversa com a
 * equipe. Mora no casco da área, então segue a pessoa por todas as telas. O fluxo ao vivo fica
 * ligado enquanto há conversa em andamento — mesmo com o painel fechado —, para o botão avisar
 * quando a equipe responde.
 */
export function ChatDoCliente() {
  const estado = useEstadoDoChat();
  const [aberto, setAberto] = useState(false);
  const [naoLidas, setNaoLidas] = useState(0);
  const conversa = estado.data?.conversa ?? null;
  const emAndamento = conversa !== null && conversa.estado !== "encerrada";

  function abrir() {
    setAberto(true);
    setNaoLidas(0);
  }

  if (!estado.data) return null;
  return (
    <aside className="chat-do-cliente" aria-label="Chat com a equipe de atendimento" data-testid="chat-do-cliente">
      {aberto ? (
        <section className="chat-painel" aria-label="Conversa com a equipe">
          <header className="chat-painel__topo">
            <h2 className="chat-painel__titulo">Fale com a equipe</h2>
            <Botao
              variante="fantasma"
              tamanho="pequeno"
              aria-label="Fechar o chat"
              data-testid="chat-fechar"
              onClick={() => setAberto(false)}
            >
              Fechar
            </Botao>
          </header>
          <CorpoDoChat estado={estado.data} conversa={conversa} />
        </section>
      ) : null}
      {emAndamento && !aberto ? (
        <Escuta conversaId={conversa.id} aoFalaDaEquipe={() => setNaoLidas((n) => n + 1)} />
      ) : null}
      {aberto ? null : (
        <button type="button" className="chat-botao" data-testid="chat-abrir" onClick={abrir}>
          Chat com a equipe
          {naoLidas > 0 ? (
            <span className="chat-botao__contagem" data-testid="chat-nao-lidas">
              {naoLidas}
            </span>
          ) : null}
        </button>
      )}
    </aside>
  );
}

/**
 * Mantém o fluxo ligado com o painel fechado e conta o que a equipe disse nesse meio-tempo.
 * Espera o histórico carregar antes de ouvir: o fluxo reenvia a conversa desde o começo, e o que a
 * tela já tem não é "não lido".
 */
function Escuta({ conversaId, aoFalaDaEquipe }: { conversaId: string; aoFalaDaEquipe: () => void }) {
  const cliente = useQueryClient();
  const historico = useMensagensDaConversa("cliente", conversaId);
  const ultimo = useRef(aoFalaDaEquipe);
  useEffect(() => {
    ultimo.current = aoFalaDaEquipe;
  });
  useConversaAoVivo("cliente", conversaId, historico.isSuccess, (quadro) => {
    if (quadro.tipo !== "mensagem") return;
    const chave = [...CHAVE_DO_WEBCHAT, "mensagens", "cliente", conversaId];
    const conhecidas = cliente.getQueryData<MensagemDoChat[]>(chave) ?? [];
    if (conhecidas.some((fala) => fala.id === quadro.mensagem.id)) return;
    acrescentarMensagem(cliente, "cliente", conversaId, quadro.mensagem);
    if (quadro.mensagem.autor_tipo === "agente") ultimo.current();
    if (mudaOEstado(quadro.mensagem)) void cliente.invalidateQueries({ queryKey: CHAVE_DO_WEBCHAT });
  });
  return null;
}

function CorpoDoChat({ estado, conversa }: { estado: EstadoDoChat; conversa: ConversaDoCliente | null }) {
  if (conversa === null) return <Inicio estado={estado} />;
  if (conversa.estado === "encerrada") return <Avaliacao conversa={conversa} />;
  return <EmAndamento conversa={conversa} />;
}

function Inicio({ estado }: { estado: EstadoDoChat }) {
  const abrir = useAbrirConversa();
  const [texto, setTexto] = useState("");

  if (!estado.disponivel) {
    return (
      <div className="chat-painel__corpo">
        <Aviso tom="marca" data-testid="chat-indisponivel">
          O chat não está disponível agora. Abra um chamado e a equipe responde por lá.
        </Aviso>
        <Link to="/app/chamados" className="botao botao--fantasma mt-3">
          Ir para os chamados
        </Link>
      </div>
    );
  }

  function iniciar(evento: FormEvent) {
    evento.preventDefault();
    if (!texto.trim()) return;
    abrir.mutate(texto.trim(), { onSuccess: () => setTexto("") });
  }

  const voltamos = frasePorVoltar(estado.proxima_abertura);
  return (
    <form className="chat-painel__corpo" onSubmit={iniciar} data-testid="chat-inicio">
      {estado.aberto_agora ? (
        <p className="chat-painel__texto">
          Conte o que está acontecendo. Em instantes alguém da equipe atende você aqui mesmo.
        </p>
      ) : (
        <Aviso tom="atencao" data-testid="chat-fora-do-horario">
          {estado.mensagem_fora_do_horario}
          {voltamos ? ` ${voltamos}` : ""}
        </Aviso>
      )}
      <textarea
        className="chat-conversa__campo"
        aria-label="O que você precisa?"
        rows={3}
        maxLength={4000}
        value={texto}
        placeholder="Escreva aqui a sua dúvida"
        onChange={(evento) => setTexto(evento.target.value)}
      />
      {abrir.isError ? (
        <p className="chat-conversa__aviso" role="alert">
          {mensagemDoConsole(abrir.error)}
        </p>
      ) : null}
      <Botao type="submit" disabled={!texto.trim() || abrir.isPending} data-testid="chat-iniciar">
        {estado.aberto_agora ? "Iniciar conversa" : "Deixar mensagem"}
      </Botao>
    </form>
  );
}

function EmAndamento({ conversa }: { conversa: ConversaDoCliente }) {
  const cancelar = useCancelarConversa(conversa.id);
  const naFila = conversa.estado === "fila";
  return (
    <div className="chat-painel__corpo chat-painel__corpo--conversa">
      <p className="chat-painel__estado" data-testid="chat-estado" data-estado={conversa.estado}>
        {naFila
          ? "Aguardando alguém da equipe…"
          : `Você está falando com ${conversa.atendente_nome ?? "a equipe"}.`}
      </p>
      <Conversa lado="cliente" conversaId={conversa.id} podeEscrever digitandoAtivo />
      {naFila ? (
        <Botao
          variante="fantasma"
          tamanho="pequeno"
          disabled={cancelar.isPending}
          data-testid="chat-cancelar"
          onClick={() => cancelar.mutate()}
        >
          Cancelar conversa
        </Botao>
      ) : null}
      {cancelar.isError ? (
        <p className="chat-conversa__aviso" role="alert">
          {mensagemDoConsole(cancelar.error)}
        </p>
      ) : null}
    </div>
  );
}

const NOTAS = [1, 2, 3, 4, 5] as const;

function Avaliacao({ conversa }: { conversa: ConversaDoCliente }) {
  const avaliar = useAvaliarConversa(conversa.id);
  const [nota, setNota] = useState<number | null>(null);
  const [comentario, setComentario] = useState("");

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    avaliar.mutate({ nota, comentario });
  }

  return (
    <form className="chat-painel__corpo" onSubmit={enviar} data-testid="chat-avaliacao">
      <p className="chat-painel__texto" data-testid="chat-encerrada">
        Conversa encerrada.
        {conversa.ticket_numero ? ` O registro ficou no chamado #${conversa.ticket_numero}.` : ""}
      </p>
      <fieldset className="chat-avaliacao__notas">
        <legend className="chat-painel__texto">Como foi o atendimento?</legend>
        {NOTAS.map((valor) => (
          <label key={valor} className="chat-avaliacao__nota">
            <input
              type="radio"
              name="nota-do-chat"
              value={valor}
              checked={nota === valor}
              onChange={() => setNota(valor)}
            />
            <span>{valor}</span>
          </label>
        ))}
      </fieldset>
      <textarea
        className="chat-conversa__campo"
        aria-label="Quer deixar um comentário? (opcional)"
        rows={2}
        maxLength={1000}
        value={comentario}
        placeholder="Quer deixar um comentário? (opcional)"
        onChange={(evento) => setComentario(evento.target.value)}
      />
      {avaliar.isError ? (
        <p className="chat-conversa__aviso" role="alert">
          {mensagemDoConsole(avaliar.error)}
        </p>
      ) : null}
      <div className="acoes-do-console">
        <Botao type="submit" disabled={nota === null || avaliar.isPending} data-testid="chat-avaliar">
          Enviar avaliação
        </Botao>
        <Botao
          variante="fantasma"
          disabled={avaliar.isPending}
          data-testid="chat-agora-nao"
          onClick={() => avaliar.mutate({ nota: null, comentario: "" })}
        >
          Agora não
        </Botao>
      </div>
    </form>
  );
}
